import type { Schemas } from "./api"

export type ObjectIdentity = Schemas["ObjectIdentity"]
export type LinkEdge = Schemas["LinkEdge"]
export type LinkPage = Schemas["LinkPage"]

export const nodeId = (o: ObjectIdentity) => `${o.objectType}/${o.key}`

/** Where one node's expansion stands. A cursor means "more may exist", whatever the page size. */
export type Expansion = { readonly state: "collapsed" } | { readonly state: "loading" } | { readonly state: "open"; readonly cursor: string | null; readonly pages: number } | { readonly state: "failed"; readonly error: unknown; readonly cursor: string | null }

export interface Neighborhood {
  readonly root: string
  readonly nodes: ReadonlyMap<string, ObjectIdentity>
  /** Edges by `from|linkType|linkId|to`, so repeated pages never duplicate an edge. */
  readonly edges: ReadonlyMap<string, LinkEdge>
  readonly expansion: ReadonlyMap<string, Expansion>
  /** Links not drawn because their far end would pass the node bound. */
  readonly withheld: number
}

/** The most nodes the explorer holds at once; it never loads a whole instance graph. */
export const NODE_BOUND = 60

export function start(root: ObjectIdentity): Neighborhood {
  const id = nodeId(root)
  return { root: id, nodes: new Map([[id, root]]), edges: new Map(), expansion: new Map([[id, { state: "collapsed" }]]), withheld: 0 }
}

export function setExpansion(n: Neighborhood, id: string, e: Expansion): Neighborhood {
  return { ...n, expansion: new Map(n.expansion).set(id, e) }
}

/**
 * Merge one authorized page of a node's links. An empty or short page with a cursor is not the end;
 * only a null cursor is. New neighbours past the bound are counted, not added.
 */
export function addPage(n: Neighborhood, id: string, page: LinkPage): Neighborhood {
  const nodes = new Map(n.nodes)
  const edges = new Map(n.edges)
  let withheld = n.withheld
  for (const edge of page.items) {
    // Admit an edge only with both ends, so the bound never leaves a half-added neighbour behind.
    const missing = [...new Map([edge.from, edge.to].filter((end) => !nodes.has(nodeId(end))).map((end) => [nodeId(end), end] as const))]
    if (nodes.size + missing.length > NODE_BOUND) {
      withheld += 1
      continue
    }
    for (const [key, end] of missing) nodes.set(key, end)
    edges.set(`${nodeId(edge.from)}|${edge.linkType}|${edge.linkId}|${nodeId(edge.to)}`, edge)
  }
  const previous = n.expansion.get(id)
  const pages = previous?.state === "open" ? previous.pages + 1 : 1
  const expansion = new Map(n.expansion).set(id, { state: "open", cursor: page.nextCursor, pages } as const)
  for (const key of nodes.keys()) if (!expansion.has(key)) expansion.set(key, { state: "collapsed" })
  return { ...n, nodes, edges, expansion, withheld }
}
